import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, Checkbox, TextInput } from '@doxyme/ui';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectInstitution, } from '../../../../../../../store/features/system';
import { API } from '../../../../../../../vendor/api';
import { OnPageLoader } from '../../../../../components/loading';
import './locations-group-access-modal.scss';
export const LocationsGroupAccessModal = ({ isOpen, onClose, locationId, locationName }) => {
    const intl = useIntl();
    const { id: institutionId } = useSelector(selectInstitution);
    const [users, setUsers] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [pendingUserIds, setPendingUserIds] = useState([]);
    const [pendingRoomIds, setPendingRoomIds] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const modalTitle = useMemo(() => {
        return intl.formatMessage({ id: 'account.settings.locations.group-access.modal-title' }, { name: locationName });
    }, [intl, locationName]);
    const resetState = useCallback(() => {
        setUsers([]);
        setRooms([]);
        setIsLoading(false);
        setErrorMessage(null);
        setPendingUserIds([]);
        setPendingRoomIds([]);
        setSearchQuery('');
    }, []);
    useEffect(() => {
        if (!isOpen) {
            resetState();
            return;
        }
        const fetchAccess = async () => {
            setIsLoading(true);
            setErrorMessage(null);
            try {
                const access = await API.institution.getLocationAccess({
                    institutionId,
                    locationId,
                });
                const { users: accessUsers = [], rooms: accessRooms = [] } = access;
                // Selected (inGroup) first, then alphabetically. Sorted once on load so
                // the order stays fixed while toggling checkboxes.
                setUsers([...accessUsers].sort((a, b) => Number(b.inGroup) - Number(a.inGroup) ||
                    (a.name ?? '').localeCompare(b.name ?? '')));
                setRooms([...accessRooms].sort((a, b) => Number(b.inGroup) - Number(a.inGroup) ||
                    (a.slug ?? '').localeCompare(b.slug ?? '')));
            }
            catch (error) {
                console.error(error);
                setErrorMessage(intl.formatMessage({
                    id: 'account.settings.locations.group-access.load-error',
                }));
            }
            finally {
                setIsLoading(false);
            }
        };
        void fetchAccess();
    }, [institutionId, intl, isOpen, locationId, resetState]);
    const closeModal = useCallback(() => {
        onClose();
        resetState();
    }, [onClose, resetState]);
    const toggleUser = useCallback(async (userId, inGroup) => {
        setPendingUserIds((prev) => prev.includes(userId) ? prev : [...prev, userId]);
        setErrorMessage(null);
        try {
            const updateUserAccess = inGroup
                ? async (params) => API.userGroups.removeFromUser(params)
                : async (params) => API.userGroups.addToUser(params);
            await updateUserAccess({
                institutionId,
                userId,
                groupId: locationId,
            });
            setUsers((prev) => prev.map((user) => user.id === userId
                ? {
                    ...user,
                    inGroup: !inGroup,
                }
                : user));
        }
        catch (error) {
            console.error(error);
            setErrorMessage(intl.formatMessage({
                id: 'account.settings.locations.group-access.update-error',
            }));
        }
        finally {
            setPendingUserIds((prev) => prev.filter((id) => id !== userId));
        }
    }, [institutionId, intl, locationId]);
    const toggleRoom = useCallback(async (roomId, inGroup) => {
        setPendingRoomIds((prev) => prev.includes(roomId) ? prev : [...prev, roomId]);
        setErrorMessage(null);
        try {
            const updateRoomAccess = inGroup
                ? async (params) => API.roomGroups.removeFromRoom(params)
                : async (params) => API.roomGroups.addToRoom(params);
            await updateRoomAccess({
                institutionId,
                roomId,
                groupId: locationId,
            });
            setRooms((prev) => prev.map((room) => room.id === roomId
                ? {
                    ...room,
                    inGroup: !inGroup,
                }
                : room));
        }
        catch (error) {
            console.error(error);
            setErrorMessage(intl.formatMessage({
                id: 'account.settings.locations.group-access.update-error',
            }));
        }
        finally {
            setPendingRoomIds((prev) => prev.filter((id) => id !== roomId));
        }
    }, [institutionId, intl, locationId]);
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const filteredUsers = useMemo(() => {
        if (!normalizedSearch) {
            return users;
        }
        return users.filter((user) => {
            const nameMatch = user.name?.toLowerCase().includes(normalizedSearch);
            const emailMatch = user.email?.toLowerCase().includes(normalizedSearch);
            return nameMatch || emailMatch;
        });
    }, [users, normalizedSearch]);
    const filteredRooms = useMemo(() => {
        if (!normalizedSearch) {
            return rooms;
        }
        return rooms.filter((room) => {
            const nameMatch = room.name?.toLowerCase().includes(normalizedSearch);
            const slugMatch = room.slug?.toLowerCase().includes(normalizedSearch);
            return nameMatch || slugMatch;
        });
    }, [rooms, normalizedSearch]);
    return (<Modal isOpen={isOpen} closeModal={closeModal} className="locations-group-access-modal">
      <ModalHeader onClose={closeModal}>
        <h4>{modalTitle}</h4>
      </ModalHeader>

      <div className="locations-group-access-modal__body">
        <div className="locations-group-access-modal__search">
          <TextInput value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder={intl.formatMessage({
            id: 'account.settings.locations.group-access.search-placeholder',
        })}/>
        </div>

        <p className="locations-group-access-modal__description">
          {intl.formatMessage({
            id: 'account.settings.locations.group-access.description',
        })}
        </p>

        {isLoading ? (<div className="locations-group-access-modal__loader">
            <OnPageLoader />
          </div>) : errorMessage ? (<div className="locations-group-access-modal__error">
            {errorMessage}
          </div>) : (<div className="locations-group-access-modal__content">
            <section className="locations-group-access-modal__section">
              <h5>
                {intl.formatMessage({
                id: 'account.settings.locations.group-access.users',
            })}
              </h5>
              <div className="locations-group-access-modal__list">
                {filteredUsers.length === 0 ? (<div className="locations-group-access-modal__empty">
                    {intl.formatMessage({
                    id: 'account.settings.locations.group-access.users-empty',
                })}
                  </div>) : (filteredUsers.map((user) => (<Checkbox key={user.id} id={`locations-group-user-${user.id}`} checked={user.inGroup} disabled={pendingUserIds.includes(user.id)} onChange={() => {
                    void toggleUser(user.id, user.inGroup);
                }} label={<div className="locations-group-access-modal__item-label">
                          <span>{user.name}</span>
                          <span className="locations-group-access-modal__item-meta">
                            {user.email}
                          </span>
                        </div>}/>)))}
              </div>
            </section>

            <section className="locations-group-access-modal__section">
              <h5>
                {intl.formatMessage({
                id: 'account.settings.locations.group-access.rooms',
            })}
              </h5>
              <div className="locations-group-access-modal__list">
                {filteredRooms.length === 0 ? (<div className="locations-group-access-modal__empty">
                    {intl.formatMessage({
                    id: 'account.settings.locations.group-access.rooms-empty',
                })}
                  </div>) : (filteredRooms.map((room) => (<Checkbox key={room.id} id={`locations-group-room-${room.id}`} checked={room.inGroup} disabled={pendingRoomIds.includes(room.id)} onChange={() => {
                    void toggleRoom(room.id, room.inGroup);
                }} label={<div className="locations-group-access-modal__item-label">
                          <span>{room.slug}</span>
                          <span className="locations-group-access-modal__item-meta">
                            {intl.formatMessage({
                        id: room.type === 'shared'
                            ? 'account.settings.locations.group-access.room-shared'
                            : 'account.settings.locations.group-access.room-personal',
                    })}
                          </span>
                        </div>}/>)))}
              </div>
            </section>
          </div>)}
      </div>
    </Modal>);
};
